import { z } from "zod";

export const onboardingStageSchema = z.enum([
  "queued",
  "processing_origin",
  "processing_pages",
  "extracting_facts",
  "extracting_visual_identity",
  "ready",
  "failed",
]);
export type OnboardingStage = z.infer<typeof onboardingStageSchema>;

export const onboardingJobTypeSchema = z.enum(["origin", "discovered", "fact", "visual_identity"]);
export type OnboardingJobType = z.infer<typeof onboardingJobTypeSchema>;

export const ONBOARDING_JOB_TYPES = onboardingJobTypeSchema.options;

export const ONBOARDING_FACT_CONFIDENCE_THRESHOLD = 0.85;

export type OnboardingStatusSnapshot = {
  brandStatus: "queued" | "processing" | "scanned" | "failed";
  jobCounts: Record<
    OnboardingJobType,
    { queued: number; processing: number; done: number; failed: number }
  >;
};

export const createEmptyOnboardingJobCounts = (): OnboardingStatusSnapshot["jobCounts"] => ({
  origin: { queued: 0, processing: 0, done: 0, failed: 0 },
  discovered: { queued: 0, processing: 0, done: 0, failed: 0 },
  fact: { queued: 0, processing: 0, done: 0, failed: 0 },
  visual_identity: { queued: 0, processing: 0, done: 0, failed: 0 },
});

const hasPendingJobs = (snapshot: OnboardingStatusSnapshot, jobType: OnboardingJobType) => {
  const counts = snapshot.jobCounts[jobType];
  return counts.queued > 0 || counts.processing > 0;
};

const hasAnyPendingJobs = (snapshot: OnboardingStatusSnapshot) =>
  ONBOARDING_JOB_TYPES.some((jobType) => hasPendingJobs(snapshot, jobType));

const hasFatalFailedJobs = (snapshot: OnboardingStatusSnapshot) => {
  if (hasAnyPendingJobs(snapshot)) {
    return false;
  }

  if (snapshot.jobCounts.origin.failed > 0 || snapshot.jobCounts.visual_identity.failed > 0) {
    return true;
  }

  const factCounts = snapshot.jobCounts.fact;
  return factCounts.failed > 0 && factCounts.done === 0;
};

const hasCompletedCriticalJobs = (snapshot: OnboardingStatusSnapshot) =>
  snapshot.jobCounts.origin.done > 0 &&
  snapshot.jobCounts.fact.done > 0 &&
  snapshot.jobCounts.visual_identity.done > 0;

const hasAnyProcessingJobs = (snapshot: OnboardingStatusSnapshot) =>
  ONBOARDING_JOB_TYPES.some((jobType) => snapshot.jobCounts[jobType].processing > 0);

export const deriveOnboardingStage = (snapshot: OnboardingStatusSnapshot): OnboardingStage => {
  if (hasFatalFailedJobs(snapshot)) {
    return "failed";
  }

  if (
    snapshot.brandStatus === "failed" &&
    !hasAnyPendingJobs(snapshot) &&
    !hasCompletedCriticalJobs(snapshot)
  ) {
    return "failed";
  }

  if (hasPendingJobs(snapshot, "origin")) {
    return snapshot.jobCounts.origin.processing > 0 ? "processing_origin" : "queued";
  }

  if (hasPendingJobs(snapshot, "discovered")) {
    return "processing_pages";
  }

  if (hasPendingJobs(snapshot, "fact")) {
    return "extracting_facts";
  }

  if (hasPendingJobs(snapshot, "visual_identity")) {
    return "extracting_visual_identity";
  }

  if (
    snapshot.brandStatus === "scanned" ||
    (snapshot.brandStatus === "failed" &&
      hasCompletedCriticalJobs(snapshot) &&
      deriveBrandStatus(snapshot) === "scanned")
  ) {
    return "ready";
  }

  return hasAnyPendingJobs(snapshot) || hasAnyProcessingJobs(snapshot)
    ? "processing_origin"
    : "queued";
};

export const deriveBrandStatus = (
  snapshot: OnboardingStatusSnapshot,
): "queued" | "processing" | "scanned" | "failed" => {
  if (hasFatalFailedJobs(snapshot)) {
    return "failed";
  }

  if (hasAnyProcessingJobs(snapshot)) {
    return "processing";
  }

  if (hasAnyPendingJobs(snapshot)) {
    return snapshot.jobCounts.origin.queued > 0 ? "queued" : "processing";
  }

  if (snapshot.brandStatus === "failed" && !hasCompletedCriticalJobs(snapshot)) {
    return "failed";
  }

  return "scanned";
};

export const isOnboardingReady = (snapshot: OnboardingStatusSnapshot) =>
  deriveOnboardingStage(snapshot) === "ready";
